import React, { ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppLayout } from './layouts/AppLayout';
import { Home } from './pages/Home';
import { PublicLanding } from './pages/PublicLanding';
import { PublicForm } from './pages/PublicForm';
import { ThankYou } from './pages/ThankYou';
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { Dashboard } from './pages/app/Dashboard';
import { Leads } from './pages/app/Leads';
import { LeadDetail } from './pages/app/LeadDetail';
import { Pipeline } from './pages/app/Pipeline';
import { Trash } from './pages/app/Trash';
import { Forms } from './pages/app/Forms';
import { FormEditor } from './pages/app/FormEditor';
import { Landings } from './pages/app/Landings';
import { LandingEditor } from './pages/app/LandingEditor';
import { Team } from './pages/app/Team';
import { Settings } from './pages/app/Settings';
import { Integrations } from './pages/app/Integrations';

/** Protección de la zona privada con la sesión real de Supabase. */
function RequireAuth({
  children
}: {
  children: ReactNode;
}) {
  const {
    session,
    restoring
  } = useAuth();
  const location = useLocation();
  if (restoring) {
    return <div className="flex min-h-screen w-full items-center justify-center bg-background" aria-busy="true" data-id="element-0" data-mp="2zl3dbpgpde7f">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" data-id="element-1" data-mp="33nq73vwxp979" />
        <span className="sr-only" data-id="element-2" data-mp="38kqbm2e4y3w9">Cargando tu espacio de trabajo…</span>
      </div>;
  }
  if (!session) {
    return <Navigate to="/login" state={{
      from: location.pathname
    }} replace data-id="element-3" data-mp="3dmfqxu8e53q5" />;
  }
  return <>{children}</>;
}
export function App() {
  return <BrowserRouter data-id="element-4" data-mp="sund47ok4egl">
      <AuthProvider data-id="element-5" data-mp="su445ggvx0ep">
        <Routes data-id="element-6" data-mp="stkok6epio3u">
          {/* Zona pública */}
          <Route path="/" element={<Home data-id="element-8" data-mp="35ypel6klo1z" />} data-id="element-7" data-mp="swgm3tmn7arr" />
          <Route path="/landing/:slug" element={<PublicLanding data-id="element-10" data-mp="38kxclxdi5kq" />} data-id="element-9" data-mp="35gm54lhq9kk" />
          <Route path="/formulario/:slug" element={<PublicForm data-id="element-12" data-mp="3ubztuaw15jen" />} data-id="element-11" data-mp="3ud1ff9ao9oam" />
          <Route path="/gracias/:slug" element={<ThankYou data-id="element-14" data-mp="d57h17dyqql6" />} data-id="element-13" data-mp="3uevq7us8c00z" />
          <Route path="/login" element={<Login data-id="element-16" data-mp="d2l9v8uzid3h" />} data-id="element-15" data-mp="d3cu5nj4soeg" />
          <Route path="/registro" element={<Register data-id="element-18" data-mp="85bc95xfyv4k" />} data-id="element-17" data-mp="85l1usekr09q" />
          <Route path="/recuperar-contrasena" element={<ForgotPassword data-id="element-20" data-mp="3gleakl2nz7g8" />} data-id="element-19" data-mp="8648hcyry28g" />

          {/* Zona privada */}
          <Route path="/app" element={<RequireAuth data-id="element-22" data-mp="3bncpxvplb02n">
                <AppLayout data-id="element-23" data-mp="3bo4lx7n3mztg" />
              </RequireAuth>} data-id="element-21" data-mp="3glnxu0wbr0i8">
            <Route index element={<Navigate to="/app/dashboard" replace data-id="element-25" data-mp="3qcinzgcoyk8y" />} data-id="element-24" data-mp="3qbzgktko3e96" />
            <Route path="dashboard" element={<Dashboard data-id="element-27" data-mp="3liwl2fwbxuqc" />} data-id="element-26" data-mp="3lk7mi92ayqea" />
            <Route path="leads" element={<Leads data-id="element-29" data-mp="2xuda1slwb3uk" />} data-id="element-28" data-mp="3limqef0cazeb" />
            <Route path="leads/:id" element={<LeadDetail data-id="element-31" data-mp="2xssh27f8bsyt" />} data-id="element-30" data-mp="2xtv8qhkdlzfs" />
            <Route path="pipeline" element={<Pipeline data-id="element-33" data-mp="2suqzxs1d63pz" />} data-id="element-32" data-mp="2sw207hvlotcc" />
            <Route path="papelera" element={<Trash data-id="element-35" data-mp="18drjrx1hphec" />} data-id="element-34" data-mp="2svjxq3ichivz" />
            <Route path="formularios" element={<Forms data-id="element-37" data-mp="18c6qsbutq6il" />} data-id="element-36" data-mp="18dhqo0l5eu4f" />
            <Route path="formularios/nuevo" element={<Forms data-id="element-39" data-mp="1d4rdktfbbjcm" />} data-id="element-38" data-mp="18byasb3eml36" />
            <Route path="formularios/:id" element={<FormEditor data-id="element-41" data-mp="1d6atgix23sdv" />} data-id="element-40" data-mp="1d62iiwkhux5b" />
            <Route path="landings" element={<Landings data-id="element-43" data-mp="yh6cf51zzu7c" />} data-id="element-42" data-mp="ygd2jv7qsokz" />
            <Route path="landings/nueva" element={<Landings data-id="element-45" data-mp="13e529bempezq" />} data-id="element-44" data-mp="ydqvdworkb3a" />
            <Route path="landings/:id/editor" element={<LandingEditor data-id="element-47" data-mp="1r499dsr0zwvh" />} data-id="element-46" data-mp="13fg61b82qmqu" />
            <Route path="equipo" element={<Team data-id="element-49" data-mp="1r4seg8vkuqs3" />} data-id="element-48" data-mp="1r3zim2y5ploq" />
            <Route path="configuracion" element={<Settings data-id="element-51" data-mp="1w3vl1xr7h526" />} data-id="element-50" data-mp="1r2g0e6sxx0kb" />
            <Route path="integraciones" element={<Integrations data-id="element-53" data-mp="1w0zp2eyx4qgd" />} data-id="element-52" data-mp="1w2122ozhvn0q" />
          </Route>

          <Route path="*" element={<Navigate to="/" replace data-id="element-55" data-mp="1i31elknpxe65" />} data-id="element-54" data-mp="1i2j7fszipfjg" />
        </Routes>
      </AuthProvider>
    </BrowserRouter>;
}